import { useState } from "react";
import "../date-range-pill.css";
import {
  MONTH_NAMES,
  WEEKDAYS,
  cellClass,
  fromKey,
  monthCells,
  monthStart,
  shiftMonths,
  toKey,
  type DayRange,
  type RangePreset,
} from "./model.js";

/**
 * The date range: presets on the left, two months of calendar on the right.
 *
 * Drawn behind the report's date pill, and inline in the export popup, which
 * is why it reports a finished range rather than closing anything itself.
 */
export function DateRangePanel({
  from,
  to,
  today,
  presets,
  onPick,
}: {
  readonly from: string;
  readonly to: string;
  /** The latest day a reader may choose; later ones are greyed. */
  readonly today: string;
  readonly presets: readonly RangePreset[];
  /** A whole range: a preset, or the second of two clicks on the calendar. */
  readonly onPick: (range: DayRange) => void;
}) {
  const [leftMonth, setLeftMonth] = useState(() => shiftMonths(monthStart(to), -1));
  /*
   * A half-made range: the first click sets an anchor and the second closes it.
   * Held here rather than pushed to the URL, because a range with one end is
   * not a range anybody should be able to bookmark or reload into.
   */
  const [anchor, setAnchor] = useState<string | null>(null);

  const months = [leftMonth, shiftMonths(leftMonth, 1)];

  return (
    <div className="daterange">
      <ul className="daterange-presets">
        {presets.map((preset) => (
          <li key={preset.label}>
            <button
              type="button"
              className={
                preset.from === from && preset.to === to
                  ? "daterange-preset daterange-preset-on"
                  : "daterange-preset"
              }
              onClick={() => {
                setAnchor(null);
                onPick({ from: preset.from, to: preset.to });
              }}
            >
              {preset.label}
            </button>
          </li>
        ))}
      </ul>

      <div className="daterange-cal">
        <div className="daterange-nav">
          <button
            type="button"
            className="daterange-step"
            aria-label="Previous month"
            onClick={() => {
              setLeftMonth((month) => shiftMonths(month, -1));
            }}
          >
            ‹
          </button>
          <div className="daterange-titles">
            {months.map((month) => (
              <span className="daterange-title" key={toKey(month)}>
                {MONTH_NAMES[month.getUTCMonth()]} {month.getUTCFullYear()}
              </span>
            ))}
          </div>
          <button
            type="button"
            className="daterange-step"
            aria-label="Next month"
            onClick={() => {
              setLeftMonth((month) => shiftMonths(month, 1));
            }}
          >
            ›
          </button>
        </div>

        <div className="daterange-months">
          {months.map((month) => (
            <div className="daterange-month" key={toKey(month)}>
              <div className="daterange-weekdays">
                {WEEKDAYS.map((weekday) => (
                  <span key={weekday}>{weekday}</span>
                ))}
              </div>
              <div className="daterange-grid">
                {monthCells(month).map((key, index) =>
                  key === null ? (
                    /* A blank has no identity beyond its position, which
                       is the one case an index key is the right key. */
                    <span key={`pad-${String(index)}`} />
                  ) : (
                    <button
                      type="button"
                      key={key}
                      className={cellClass(key, { from, to, anchor, today })}
                      disabled={key > today}
                      onClick={() => {
                        if (anchor === null) {
                          setAnchor(key);
                          return;
                        }
                        // Either order of clicks makes the same range.
                        const next =
                          key < anchor
                            ? { from: key, to: anchor }
                            : { from: anchor, to: key };
                        setAnchor(null);
                        onPick(next);
                      }}
                    >
                      {fromKey(key).getUTCDate()}
                    </button>
                  ),
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
